<template>
  <v-data-table
    :class="{ laptop, laptopSmaller }"
    :headers="headers"
    :items="filteredData"
    item-key="id"
    sort-by="fullName"
    :header-props="{ sortIcon: null }"
    show-expand
    :items-per-page="50"
    :footer-props="{
      'items-per-page-options': [10, 50, 100],
    }"
    @click:row="handleEditForm($event)"
  >
    <!-- LinkedIn cell override -->
    <template v-slot:[`item.linkedIn`]="{ value }">
      <v-btn
        icon
        large
        color="blue darken-2"
        :disabled="!value"
        @click.stop="openLinkedInProfile(value)"
        ><v-icon>mdi-linkedin</v-icon></v-btn
      >
    </template>

    <!-- Availability cell override -->
    <template v-slot:[`item.availability`]="{ value }">
      <v-tooltip right color="primary" :disabled="value?.length <= 20">
        <template v-slot:activator="{ on, attrs }">
          <span v-bind="attrs" v-on="on">{{ ellipseText(value, 20) }}</span>
        </template>
        <span>{{ value }}</span>
      </v-tooltip>
    </template>

    <!-- CV cell override -->
    <template v-slot:[`item.cvRef`]="{ item, value }">
      <v-btn
        icon
        large
        color="secondary"
        :disabled="!value"
        :loading="item.cvDownloading"
        @click.stop="downloadCv(item)"
        ><v-icon>mdi-file-download</v-icon></v-btn
      >
    </template>

    <!-- Notes cell override -->
    <template v-slot:[`item.notes`]="{ value }">
      <v-tooltip right color="primary" :disabled="value?.length <= 30">
        <template v-slot:activator="{ on, attrs }">
          <span v-bind="attrs" v-on="on">{{ ellipseText(value, 30) }}</span>
        </template>
        <span>{{ value }}</span>
      </v-tooltip>
    </template>

    <!-- Country cell override -->
    <template v-slot:[`item.country`]="{ value }">
      <v-img
        max-width="48"
        width="48"
        :height="getFlag(value) === noCountry ? 48 : 36"
        :src="getFlag(value)"
        :alt="`${value}_flag`"
      ></v-img>
    </template>

    <!-- Status cell override -->
    <template v-slot:[`item.status`]="{ value }">
      <v-chip v-if="value" :color="statusesColors[value]">{{ value }}</v-chip>
    </template>

    <!-- Expanded panel -->
    <template v-slot:expanded-item="{ headers, item }">
      <td :colspan="headers.length">
        <SourcingTableExpansion v-on="$listeners" :candidateInfo="item" />
      </td>
    </template>
  </v-data-table>
</template>

<script>
// vuex
import { mapGetters } from 'vuex';
// libraries
import Fuse from 'fuse.js';
import { saveAs } from 'file-saver';
// internal
import { customBreakpoint } from '@/utils/mixins';
import { getFileBlob } from '@/utils/dbUtils';
import { ISO_COUNTRIES } from '@/utils/constants';
import noCountry from '@/assets/logo.png';
// components
import SourcingTableExpansion from './SourcingTableExpansion.vue';

export default {
  name: 'SourcingTable',
  components: {
    SourcingTableExpansion,
  },
  props: {
    tableData: {
      type: Array,
      required: false,
    },
    filterSpecialty: {
      type: String,
      required: false,
    },
    filterExperience: {
      type: String,
      required: false,
    },
    filterTechLevel: {
      type: String,
      required: false,
    },
    filterSkills: {
      type: Array,
      required: false,
    },
    filterCountry: {
      type: String,
      required: false,
    },
    filterStatus: {
      type: String,
      required: false,
    },
    filterSearch: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      headers: [
        { text: 'Candidate', value: 'fullName' },
        { text: 'Specialty', value: 'specialty' },
        { text: 'Years of Experience', value: 'experience' },
        { text: 'Tech Level', value: 'techLevel' },
        { text: 'English', value: 'eng' },
        { text: 'LinkedIn', value: 'linkedIn' },
        { text: 'Minimal hourly rate, $', value: 'minSalary' },
        { text: 'Desired hourly rate, $', value: 'maxSalary' },
        { text: 'Availability', value: 'availability' },
        { text: 'CV', value: 'cvRef' },
        { text: 'Notes', value: 'notes' },
        { text: 'Location', value: 'country' },
        { text: 'Status', value: 'status' },
        { text: '', value: 'data-table-expand' },
      ],
      statusesColors: {
        Sourced: '#fff',
        Communication: '#F0F4C3',
        'Screening Scheduled': '#E1F5FE',
        'Screening Passed': '#B3E5FC',
        'Tech Interview Scheduled': '#E8EAF6',
        'Tech Interview Passed': '#C5CAE9',
        Rejected: '#EF9A9A',
        Negotiating: '#C8E6C9',
      },
      noCountry,
    };
  },
  mixins: [customBreakpoint],
  computed: {
    ...mapGetters('auth', ['isSales']),
    tableFieldValues() {
      if (this.tableData.length) {
        const fields = Object.entries(this.tableData[0]).reduce(
          (fields, entry) => {
            const nestedKey = entry[1]
              ? Object.keys(entry[1]).map(
                  (key) => `${entry[0]}${isNaN(key) ? `.${key}` : ''}`
                )
              : [];
            return [...fields, ...nestedKey];
          },
          []
        );
        return fields;
      } else {
        return [];
      }
    },
    filteredData() {
      let dateFilteredData = this.tableData;

      if (this.filterSpecialty)
        dateFilteredData = dateFilteredData.filter(
          (el) => el.specialty === this.filterSpecialty
        );

      if (this.filterExperience)
        dateFilteredData = dateFilteredData.filter(
          (el) => el.experience === this.filterExperience
        );

      if (this.filterTechLevel)
        dateFilteredData = dateFilteredData.filter(
          (el) => el.techLevel === this.filterTechLevel
        );

      if (this.filterSkills.length)
        dateFilteredData = dateFilteredData.filter((el) =>
          this.filterSkills.every((s) => el.skills.includes(s))
        );

      if (this.filterCountry)
        dateFilteredData = dateFilteredData.filter(
          (el) => el.country === this.filterCountry
        );

      if (this.filterStatus)
        dateFilteredData = dateFilteredData.filter(
          (el) => el.status === this.filterStatus
        );

      const searchOptions = {
        includeScore: true,
        threshold: 0,
        ignoreLocation: true,
        keys: this.tableFieldValues,
      };

      if (this.filterSearch) {
        const fuse = new Fuse(dateFilteredData, searchOptions);
        dateFilteredData = fuse.search(this.filterSearch).map((el) => el.item);
      }

      return dateFilteredData;
    },
  },
  methods: {
    handleEditForm(data) {
      if (!this.isSales) this.$emit('call-edit-form', data);
    },
    openLinkedInProfile(link) {
      window.open(link, '_blank');
    },
    downloadCv(item) {
      const index = this.tableData.indexOf(item);
      this.tableData[index].cvDownloading = true;
      getFileBlob(`${item.id}_cvFile`).then((blob) => {
        const newBlob = new Blob([blob], {
          type: 'application/pdf',
        });
        this.tableData[index].cvDownloading = false;
        saveAs(newBlob, `${item.fullName}'s CV`);
      });
    },
    ellipseText(text, length) {
      if (text?.length > length) return text.substring(0, length) + '...';
      else return text;
    },
    getFlag(country) {
      const isoCountry = Object.keys(ISO_COUNTRIES).find((item) =>
        item.includes(country)
      );
      return isoCountry
        ? `https://flagcdn.com/192x144/${ISO_COUNTRIES[
            isoCountry
          ].toLowerCase()}.png`
        : noCountry;
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  tbody > tr:not(.v-data-table__expanded__content) {
    cursor: pointer;
  }
}
</style>
